import React, { useContext } from 'react'

import classNames from 'classnames'

import Button from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import type { ProfileImageProps } from '~/components/atoms/ProfileImage/ProfileImage'
import Spacer, { SpacerSizes } from '~/components/atoms/Spacer/Spacer'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import ArtistSelect from '~/components/forms/ArtistSelect/ArtistSelect'
import { ComponentTheme } from '~/components/themes'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import { useSettingsContext } from '~/contexts/settingsContext'
import type { ListOptionProps } from '~/data/forms/shared/interfaces/Form'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'

import styles from './RequestValuation.module.css'

export interface RequestValuationProps {
  title?: string
  placeholderText?: string
  buttonText: string
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  moduleId?: string
  body?: string
  largeTitle?: boolean
  wide?: boolean
  value?: {
    value: string
    label: string
  }
  artist?: {
    id: string
    name: string
  }
  useShadow?: boolean
}

export default function RequestValuation({
  title,
  buttonText,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  testID,
  moduleId,
  body,
  value,
  artist,
  largeTitle = false,
  wide = false,
  useShadow = false,
}: RequestValuationProps) {
  const { allArtists } = useSettingsContext()

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault()
  }

  const { currentArtworkState } = useContext(SelectedArtworkContext)

  const getData = (id: string) => {
    artistOptions.filter(item => id === item.value)
  }

  const artistOptions: ListOptionProps[] = []
  if (allArtists) {
    allArtists.map((artist: ProfileImageProps) => {
      artistOptions.push({
        label: artist.name,
        value: artist._id,
        image: artist.image,
      })
    })
  }

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
      id={moduleId}
      className={classNames(useShadow && styles.shadow)}
    >
      <Container>
        <form
          className={classNames(styles.base, wide && styles.wideBase)}
          data-testid={testID}
          onSubmit={e => onSubmit(e)}
        >
          <fieldset>
            <legend className="visually-hidden">{title}</legend>
            <Text
              Tag="h3"
              size={TextSizes.primaryTitleLrg}
              className={classNames(
                styles.title,
                largeTitle && styles.largeTitle
              )}
            >
              {title}
            </Text>
            {body && (
              <Text
                Tag="p"
                size={TextSizes.magSecondaryTitleLrg}
                className={styles.body}
              >
                {body}
              </Text>
            )}

            {!artist && (
              <div className={styles.formSelect}>
                <ArtistSelect
                  options={artistOptions}
                  name={'artist'}
                  defaultValue={value}
                  onChange={(name: string, value: string) => getData(value)}
                  searchInsightsEvent={{
                    method: SearchAnalystMethods.convertedFilters,
                    name: SearchAnalystEventNames.sellArtistSelected,
                  }}
                />
              </div>
            )}

            <Spacer size={SpacerSizes.md} />
            <Button
              iconLeft={true}
              launchForm={FormTypes.valuationKnownArtist}
              disabled={currentArtworkState?.artist?.id === ''}
            >
              {buttonText}
            </Button>
          </fieldset>
        </form>
      </Container>
    </Fixture>
  )
}
